/**
 * Admin Project Detail Page
 *
 * Allows editing project details, managing tokens, and assigning collections.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getAdminProject,
  getAdminProjectStats,
  updateAdminProject,
  getAdminTags,
  addProjectToken,
  removeProjectToken,
  refreshProjectToken,
  getUnassignedCollections,
  assignCollectionToProject,
  updateProjectCollectionOrder,
  uploadProjectImage,
  RICHLIST_STRATEGIES,
  type AdminProject,
  type AdminProjectStats,
  type AdminProjectTag,
  type AdminProjectToken,
  type UpdateProjectRequest,
  type AddTokenRequest,
  type UnassignedCollection,
  type RichlistPointsStrategy,
  type CollectionsConfig,
  type ProjectFeature,
} from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import { formatPrice } from '../utils/formatters.js';

/**
 * Sortable collection item component for drag-and-drop reordering
 */
interface SortableCollectionItemProps {
  collection: NonNullable<AdminProject['collections']>[number];
  onUnassign: (id: string, name: string) => void;
}

function SortableCollectionItem({ collection, onUnassign }: SortableCollectionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: collection.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between rounded-lg border border-gray-200 p-4 bg-white"
    >
      <div className="flex items-center gap-3">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1"
          title="Drag to reorder"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="9" cy="5" r="1" />
            <circle cx="9" cy="12" r="1" />
            <circle cx="9" cy="19" r="1" />
            <circle cx="15" cy="5" r="1" />
            <circle cx="15" cy="12" r="1" />
            <circle cx="15" cy="19" r="1" />
          </svg>
        </button>
        <div>
          <div className="font-medium text-gray-900">{collection.name}</div>
          <div className="text-sm text-gray-500">
            /{collection.slug} &bull; {collection.total_supply} NFTs
          </div>
        </div>
      </div>
      <button
        onClick={() => onUnassign(collection.id, collection.name)}
        className="text-sm text-red-600 hover:text-red-800"
      >
        Unassign
      </button>
    </div>
  );
}

function formatPercent(numerator: number, denominator: number): string {
  if (!denominator) return '0%';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

interface QuickStatsCardProps {
  stats: AdminProjectStats | null;
  loading: boolean;
}

function QuickStatsCard({ stats, loading }: QuickStatsCardProps) {
  const items = [
    {
      label: 'Team Members',
      primary: stats ? stats.team_members.toLocaleString() : '—',
      secondary: null,
    },
    {
      label: 'Collections',
      primary: stats ? stats.collections.toLocaleString() : '—',
      secondary: null,
    },
    {
      label: 'Total NFT Holders',
      primary: stats ? stats.total_holders.toLocaleString() : '—',
      secondary: null,
    },
    {
      label: 'NFTs Listed',
      primary: stats ? `${stats.listed_nfts.toLocaleString()} / ${stats.total_nfts.toLocaleString()}` : '—',
      secondary: stats ? formatPercent(stats.listed_nfts, stats.total_nfts) : null,
    },
    {
      label: 'XRP Deals Users',
      primary: stats ? stats.xrp_deals_users.toLocaleString() : '—',
      secondary: stats ? formatPercent(stats.xrp_deals_users, stats.total_holders) : null,
    },
    {
      label: 'Copied Listings',
      primary: stats ? stats.copied_listings_users.toLocaleString() : '—',
      secondary: stats ? formatPercent(stats.copied_listings_users, stats.total_holders) : null,
      hint: '% of holders with no un-migrated listings on other marketplaces',
    },
    {
      label: 'Best Price on Deals',
      primary: stats ? `${stats.deals_best_price_nfts.toLocaleString()} / ${stats.listed_nfts.toLocaleString()}` : '—',
      secondary: stats ? formatPercent(stats.deals_best_price_nfts, stats.listed_nfts) : null,
      hint: '% of listed NFTs with a deals offer at the lowest price (or tied)',
    },
  ];

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Quick Stats</h2>
        {loading && <span className="text-xs text-gray-500">Loading…</span>}
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((item) => (
          <div key={item.label} className="min-w-0" title={item.hint}>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              {item.label}
            </dt>
            <dd className="mt-1 text-xl font-semibold text-gray-900">{item.primary}</dd>
            {item.secondary && (
              <dd className="mt-0.5 text-sm text-blue-600">{item.secondary}</dd>
            )}
          </div>
        ))}
      </dl>
    </div>
  );
}

export function AdminProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<AdminProject | null>(null);
  const [tags, setTags] = useState<AdminProjectTag[]>([]);
  const [unassignedCollections, setUnassignedCollections] = useState<UnassignedCollection[]>([]);
  const [stats, setStats] = useState<AdminProjectStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState<UpdateProjectRequest>({});
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  // Token modal
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenForm, setTokenForm] = useState<AddTokenRequest>({
    currency_code: '',
    issuer_address: '',
    display_name: '',
  });
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Collection assignment
  const [assigningCollection, setAssigningCollection] = useState(false);
  const [collectionFilter, setCollectionFilter] = useState('');

  // Image upload
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [bannerUploadError, setBannerUploadError] = useState<string | null>(null);

  // Token refresh
  const [refreshingTokenId, setRefreshingTokenId] = useState<string | null>(null);

  // Richlist config
  const [savingRichlistConfig, setSavingRichlistConfig] = useState(false);

  // Feature flags
  const [savingFeatureFlags, setSavingFeatureFlags] = useState(false);

  // Collection order
  const [savingCollectionOrder, setSavingCollectionOrder] = useState(false);

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !project?.collections || !id) {
      return;
    }

    const oldIndex = project.collections.findIndex((c) => c.id === active.id);
    const newIndex = project.collections.findIndex((c) => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically update local state
    const newCollections = arrayMove(project.collections, oldIndex, newIndex);
    setProject({ ...project, collections: newCollections });

    // Save to server
    setSavingCollectionOrder(true);
    try {
      await updateProjectCollectionOrder(id, newCollections.map((c) => c.id));
    } catch (err) {
      console.error('Failed to save collection order:', err);
      // Revert on error
      setProject({ ...project });
    } finally {
      setSavingCollectionOrder(false);
    }
  };

  const handleRichlistConfigChange = async (collectionId: string, strategy: RichlistPointsStrategy | undefined) => {
    if (!id || !project) return;

    setSavingRichlistConfig(true);
    try {
      const currentConfig = project.collections_config || {};
      const newConfig: CollectionsConfig = {
        ...currentConfig,
        [collectionId]: {
          ...currentConfig[collectionId],
          richlistPointsStrategy: strategy,
        },
      };

      await updateAdminProject(id, { collections_config: newConfig });
      // Only update collections_config in state, preserve tokens and collections arrays
      setProject(prev => prev ? { ...prev, collections_config: newConfig } : prev);
    } catch (err) {
      console.error('Failed to update richlist config:', err);
    } finally {
      setSavingRichlistConfig(false);
    }
  };

  const handleToggleFeature = async (feature: ProjectFeature, enabled: boolean) => {
    if (!id || !project) return;

    setSavingFeatureFlags(true);
    try {
      const current = project.enabled_features || [];
      const newFeatures = enabled
        ? [...current, feature]
        : current.filter(f => f !== feature);

      await updateAdminProject(id, { enabled_features: newFeatures });
      setProject(prev => prev ? { ...prev, enabled_features: newFeatures } : prev);
    } catch (err) {
      console.error('Failed to update feature flags:', err);
    } finally {
      setSavingFeatureFlags(false);
    }
  };

  const handleRefreshToken = async (tokenId: string) => {
    if (!id) return;

    setRefreshingTokenId(tokenId);
    try {
      const refreshedData = await refreshProjectToken(id, tokenId);
      // Update the token data in local state
      setProject(prev => {
        if (!prev || !prev.tokens) return prev;
        return {
          ...prev,
          tokens: prev.tokens.map(t =>
            t.id === tokenId
              ? {
                  ...t,
                  holder_count: refreshedData.holder_count,
                  volume_24h_xrp: refreshedData.volume_24h_xrp,
                  amm_liquidity_xrp: refreshedData.amm_liquidity_usd ? refreshedData.amm_liquidity_usd / 2 : null, // Rough conversion
                  amm_liquidity_usd: refreshedData.amm_liquidity_usd,
                  price_xrp: refreshedData.price_xrp,
                  market_cap_usd: refreshedData.market_cap_usd,
                }
              : t
          ),
        };
      });
    } catch (err) {
      console.error('Failed to refresh token:', err);
    } finally {
      setRefreshingTokenId(null);
    }
  };

  const handleImageUpload = async (file: File, type: 'image' | 'banner') => {
    if (!id) return;

    const setUploading = type === 'image' ? setUploadingImage : setUploadingBanner;
    const setUploadError = type === 'image' ? setImageUploadError : setBannerUploadError;

    setUploading(true);
    setUploadError(null);

    try {
      const result = await uploadProjectImage(id, file, type);
      // Update the form and project state with the new URL
      if (type === 'image') {
        setEditForm(prev => ({ ...prev, image_url: result.url }));
        setProject(prev => prev ? { ...prev, image_url: result.url } : prev);
      } else {
        setEditForm(prev => ({ ...prev, banner_url: result.url }));
        setProject(prev => prev ? { ...prev, banner_url: result.url } : prev);
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const loadStats = async (projectId: string) => {
    try {
      setStatsLoading(true);
      const data = await getAdminProjectStats(projectId);
      setStats(data);
    } catch (err) {
      console.error('Failed to load project stats:', err);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [projectData, tagsData, collectionsData] = await Promise.all([
        getAdminProject(id),
        getAdminTags(),
        getUnassignedCollections(),
      ]);

      setProject(projectData);
      setTags(tagsData.tags);
      setUnassignedCollections(collectionsData.collections);

      // Kick off stats fetch in the background (don't block page render)
      loadStats(id);

      // Initialize form with project data
      setEditForm({
        name: projectData.name,
        slug: projectData.slug,
        description: projectData.description,
        website_url: projectData.website_url,
        twitter_url: projectData.twitter_url,
        discord_url: projectData.discord_url,
        image_url: projectData.image_url,
        banner_url: projectData.banner_url,
        owner_address: projectData.owner_address,
      });
      setSelectedTagIds(projectData.tags?.map(t => t.id) || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      const updateData: UpdateProjectRequest = {
        ...editForm,
        tag_ids: selectedTagIds,
      };

      await updateAdminProject(id, updateData);
      setSaveSuccess(true);
      await loadData();

      // Clear success after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleAddToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setTokenLoading(true);
      setTokenError(null);

      await addProjectToken(id, {
        currency_code: tokenForm.currency_code,
        issuer_address: tokenForm.issuer_address,
        display_name: tokenForm.display_name || null,
      });

      setShowTokenModal(false);
      setTokenForm({ currency_code: '', issuer_address: '', display_name: '' });
      await loadData();
    } catch (err: any) {
      setTokenError(err.message || 'Failed to add token');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleRemoveToken = async (tokenId: string, tokenName: string) => {
    if (!id) return;
    if (!confirm(`Remove token "${tokenName}" from this project?`)) return;

    try {
      await removeProjectToken(id, tokenId);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to remove token');
    }
  };

  const handleAssignCollection = async (collectionId: string) => {
    if (!id) return;

    try {
      setAssigningCollection(true);
      await assignCollectionToProject(collectionId, id);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to assign collection');
    } finally {
      setAssigningCollection(false);
    }
  };

  const handleUnassignCollection = async (collectionId: string, collectionName: string) => {
    if (!confirm(`Unassign "${collectionName}" from this project?`)) return;

    try {
      await assignCollectionToProject(collectionId, null);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to unassign collection');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading project...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !project) {
    return (
      <AdminLayout>
        <ErrorDisplay error={error || 'Project not found'} onRetry={loadData} />
        <div className="mt-4">
          <Link to="/admin/projects" className="text-blue-600 hover:text-blue-800">
            &larr; Back to Projects
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Link to="/admin/projects" className="text-sm text-blue-600 hover:text-blue-800">
              &larr; Back to Projects
            </Link>
            <h1 className="mt-2 text-3xl font-bold text-gray-900">{project.name}</h1>
            <p className="text-sm text-gray-500">/{project.slug}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to={`/projects/${project.slug}`}
              target="_blank"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              View Public Page
            </Link>
          </div>
        </div>

        {/* Quick Stats */}
        <QuickStatsCard stats={stats} loading={statsLoading} />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main Edit Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Details Card */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-gray-900">Project Details</h2>

              {saveError && (
                <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{saveError}</div>
              )}
              {saveSuccess && (
                <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-700">
                  Project saved successfully!
                </div>
              )}

              <form onSubmit={handleSave} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      required
                      value={editForm.name || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Slug</label>
                    <input
                      type="text"
                      required
                      value={editForm.slug || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, slug: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      pattern="[a-z0-9-]+"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={editForm.description || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Website URL</label>
                    <input
                      type="url"
                      value={editForm.website_url || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, website_url: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">X URL</label>
                    <input
                      type="url"
                      value={editForm.twitter_url || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, twitter_url: e.target.value }))}
                      placeholder="https://x.com/..."
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Discord URL</label>
                  <input
                    type="url"
                    value={editForm.discord_url || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, discord_url: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Owner Address</label>
                  <input
                    type="text"
                    value={editForm.owner_address || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, owner_address: e.target.value }))}
                    placeholder="r..."
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">XRPL address of the project owner</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Project Image</label>
                    <p className="text-xs text-gray-500 mb-1">Square format (1:1), e.g. 500x500px</p>
                    <div className="mt-1 space-y-2">
                      {/* Preview */}
                      {(editForm.image_url || project?.image_url) && (
                        <div className="relative w-24 h-24">
                          <img
                            src={editForm.image_url || project?.image_url || ''}
                            alt="Project"
                            className="w-full h-full object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      )}
                      {/* Upload button */}
                      <label className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          disabled={uploadingImage}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(file, 'image');
                          }}
                        />
                      </label>
                      {imageUploadError && (
                        <p className="text-sm text-red-600">{imageUploadError}</p>
                      )}
                      {/* URL fallback */}
                      <input
                        type="url"
                        value={editForm.image_url || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, image_url: e.target.value }))}
                        placeholder="Or enter URL directly"
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Banner Image</label>
                    <p className="text-xs text-gray-500 mb-1">Wide format (~4:1), e.g. 1200x300px</p>
                    <div className="mt-1 space-y-2">
                      {/* Preview */}
                      {(editForm.banner_url || project?.banner_url) && (
                        <div className="relative w-48 h-16">
                          <img
                            src={editForm.banner_url || project?.banner_url || ''}
                            alt="Banner"
                            className="w-full h-full object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      )}
                      {/* Upload button */}
                      <label className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer ${uploadingBanner ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        {uploadingBanner ? 'Uploading...' : 'Upload Banner'}
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          disabled={uploadingBanner}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(file, 'banner');
                          }}
                        />
                      </label>
                      {bannerUploadError && (
                        <p className="text-sm text-red-600">{bannerUploadError}</p>
                      )}
                      {/* URL fallback */}
                      <input
                        type="url"
                        value={editForm.banner_url || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, banner_url: e.target.value }))}
                        placeholder="Or enter URL directly"
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {tags.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => toggleTag(tag.id)}
                          className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                            selectedTagIds.includes(tag.id)
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>

            {/* Tokens Card */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Project Tokens</h2>
                <button
                  onClick={() => setShowTokenModal(true)}
                  className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
                >
                  + Add Token
                </button>
              </div>

              {project.tokens && project.tokens.length > 0 ? (
                <div className="space-y-3">
                  {project.tokens.map((token) => (
                    <div
                      key={token.id}
                      className="flex items-center justify-between rounded-lg border border-gray-200 p-4"
                    >
                      <div>
                        <div className="font-medium text-gray-900">
                          ${token.display_name || token.currency_code}
                        </div>
                        <div className="text-sm text-gray-500">
                          {token.currency_code} / {token.issuer_address.slice(0, 8)}...{token.issuer_address.slice(-4)}
                        </div>
                        {token.price_xrp !== null && (
                          <div className="mt-1 text-sm text-gray-600">
                            Price: {formatPrice(token.price_xrp)} XRP
                            {token.price_change_24h !== null && (
                              <span className={token.price_change_24h >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {' '}({token.price_change_24h >= 0 ? '+' : ''}{token.price_change_24h.toFixed(2)}%)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleRefreshToken(token.id)}
                          disabled={refreshingTokenId === token.id}
                          className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        >
                          {refreshingTokenId === token.id ? 'Refreshing...' : 'Refresh'}
                        </button>
                        <button
                          onClick={() => handleRemoveToken(token.id, '$' + (token.display_name || token.currency_code))}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No tokens added yet.</p>
              )}
            </div>

            {/* Collections Card */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Assigned Collections</h2>
                {savingCollectionOrder && (
                  <span className="text-sm text-gray-500">Saving order...</span>
                )}
              </div>

              {project.collections && project.collections.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={project.collections.map((c) => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {project.collections.map((collection) => (
                        <SortableCollectionItem
                          key={collection.id}
                          collection={collection}
                          onUnassign={handleUnassignCollection}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <p className="text-gray-500 text-sm">No collections assigned yet.</p>
              )}
            </div>

            {/* Richlist Config Card */}
            {project.collections && project.collections.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">Richlist</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Configure how points are calculated for each collection's richlist.
                </p>

                <div className="space-y-4">
                  {project.collections.map((collection) => {
                    const config = project.collections_config?.[collection.id];
                    const currentStrategy = config?.richlistPointsStrategy || '';

                    return (
                      <div
                        key={collection.id}
                        className="rounded-lg border border-gray-200 p-4"
                      >
                        <div className="font-medium text-gray-900 mb-2">{collection.name}</div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Richlist Points Strategy
                        </label>
                        <select
                          value={currentStrategy}
                          onChange={(e) => {
                            const value = e.target.value as RichlistPointsStrategy | '';
                            handleRichlistConfigChange(
                              collection.id,
                              value === '' ? undefined : value as RichlistPointsStrategy
                            );
                          }}
                          disabled={savingRichlistConfig}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                        >
                          <option value="">-- Not configured --</option>
                          <option value={RICHLIST_STRATEGIES.QUARTER_POINT_PER_NFT}>0.25 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.HALF_POINT_PER_NFT}>0.5 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.ONE_POINT_PER_NFT}>1 point per NFT</option>
                          <option value={RICHLIST_STRATEGIES.TWO_POINTS_PER_NFT}>2 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.TWO_AND_HALF_POINTS_PER_NFT}>2.5 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.THREE_POINTS_PER_NFT}>3 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.THREE_AND_QUARTER_POINTS_PER_NFT}>3.25 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.FOUR_POINTS_PER_NFT}>4 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.FIVE_POINTS_PER_NFT}>5 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.SIX_POINTS_PER_NFT}>6 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.SEVEN_POINTS_PER_NFT}>7 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.EIGHT_POINTS_PER_NFT}>8 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.NINE_POINTS_PER_NFT}>9 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.TEN_POINTS_PER_NFT}>10 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.HUNDRED_POINTS_PER_NFT}>100 points per NFT</option>
                          <option value={RICHLIST_STRATEGIES.WISE_GUYS}>WISE GUYS</option>
                          <option value={RICHLIST_STRATEGIES.RASCALZ}>RASCALZ (Rarity-based)</option>
                          <option value={RICHLIST_STRATEGIES.VOC}>VOC (ID-based)</option>
                          <option value={RICHLIST_STRATEGIES.PLACERS}>PLACERS (Tier-based)</option>
                          <option value={RICHLIST_STRATEGIES.BMD_HORSE_HEROES}>BMD Horse Heroes (ID-based)</option>
                          <option value={RICHLIST_STRATEGIES.CONSPIRACY_WHITE_RABBIT}>Conspiracy - White Rabbit (Rank tiers, 11-22 pts)</option>
                          <option value={RICHLIST_STRATEGIES.CONSPIRACY_AREA_589}>Conspiracy - Area 589 (Rank tiers, 11-22 pts)</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

            {/* Feature Flags Card */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Feature Flags</h2>
                {savingFeatureFlags && (
                  <span className="text-sm text-gray-500">Saving...</span>
                )}
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Enable or disable features for this project.
              </p>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={project.enabled_features?.includes('project_richlist') ?? false}
                    onChange={(e) => handleToggleFeature('project_richlist', e.target.checked)}
                    disabled={savingFeatureFlags}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Richlist</div>
                    <div className="text-xs text-gray-500">Show project richlist tab with holder rankings</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={project.enabled_features?.includes('project_rewards') ?? false}
                    onChange={(e) => handleToggleFeature('project_rewards', e.target.checked)}
                    disabled={savingFeatureFlags}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Rewards</div>
                    <div className="text-xs text-gray-500">Show rewards tab and allow owner to manage claim periods</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={project.enabled_features?.includes('ambassador_program') ?? false}
                    onChange={(e) => handleToggleFeature('ambassador_program', e.target.checked)}
                    disabled={savingFeatureFlags}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Ambassador Program</div>
                    <div className="text-xs text-gray-500">Show Promote tab on launches, allowing creators to allocate ambassador slots</div>
                  </div>
                </label>
              </div>
            </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Statistics</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm text-gray-500">Collections</dt>
                  <dd className="text-xl font-semibold text-gray-900">
                    {project.collections?.length || 0}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Tokens</dt>
                  <dd className="text-xl font-semibold text-gray-900">
                    {project.tokens?.length || 0}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Created</dt>
                  <dd className="text-sm text-gray-900">
                    {new Date(project.created_at).toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Updated</dt>
                  <dd className="text-sm text-gray-900">
                    {new Date(project.updated_at).toLocaleString()}
                  </dd>
                </div>
                {project.owner_address && (
                  <div>
                    <dt className="text-sm text-gray-500">Owner</dt>
                    <dd className="text-sm text-gray-900 font-mono truncate" title={project.owner_address}>
                      {project.owner_address}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Add Collection Card */}
            {unassignedCollections.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Add Collection</h3>
                <input
                  type="text"
                  placeholder="Filter by name or slug..."
                  value={collectionFilter}
                  onChange={(e) => setCollectionFilter(e.target.value)}
                  className="mb-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {unassignedCollections
                    .filter((collection) => {
                      if (!collectionFilter.trim()) return true;
                      const search = collectionFilter.toLowerCase();
                      return (
                        collection.name.toLowerCase().includes(search) ||
                        collection.slug.toLowerCase().includes(search)
                      );
                    })
                    .map((collection) => (
                      <button
                        key={collection.id}
                        onClick={() => handleAssignCollection(collection.id)}
                        disabled={assigningCollection}
                        className="w-full text-left rounded-md border border-gray-200 p-3 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        <div className="font-medium text-gray-900 text-sm">{collection.name}</div>
                        <div className="text-xs text-gray-500">/{collection.slug}</div>
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add Token Modal */}
        {showTokenModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <h2 className="mb-4 text-xl font-bold text-gray-900">Add Token</h2>

              {tokenError && (
                <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{tokenError}</div>
              )}

              <form onSubmit={handleAddToken} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency Code *</label>
                  <input
                    type="text"
                    required
                    value={tokenForm.currency_code}
                    onChange={(e) => setTokenForm(prev => ({ ...prev, currency_code: e.target.value.toUpperCase() }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g., USD, SOLO, XPM"
                  />
                  <p className="mt-1 text-xs text-gray-500">3-40 characters, uppercase</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Issuer Address *</label>
                  <input
                    type="text"
                    required
                    value={tokenForm.issuer_address}
                    onChange={(e) => setTokenForm(prev => ({ ...prev, issuer_address: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="r..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    type="text"
                    value={tokenForm.display_name || ''}
                    onChange={(e) => setTokenForm(prev => ({ ...prev, display_name: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Optional friendly name"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTokenModal(false);
                      setTokenError(null);
                    }}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={tokenLoading}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {tokenLoading ? 'Adding...' : 'Add Token'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
